import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { href, useNavigate } from 'react-router';

import { eventBus } from '~shared/lib';

import { NOTIFICATIONS_CONFIG } from './constants';
import { displayToast } from './displayToast';
import { NotificationType } from './types';

export type NotificationMessage = {
  type: NotificationType;
  notificationPreferences?: {
    sound?: boolean;
  };
};

type useNotificationHandlerParams = {
  pharmacyId: string;
  emit?: typeof eventBus.emit;
  onNotificationHandled?: () => void;
};

export const useNotificationHandler = ({
  pharmacyId,
  emit = eventBus.emit,
  onNotificationHandled,
}: useNotificationHandlerParams) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleNotification = useCallback(
    (data: NotificationMessage) => {
      emit(data);

      const config = NOTIFICATIONS_CONFIG[data.type];

      if (!config) {
        return;
      }

      if (data.notificationPreferences?.sound && audioRef.current) {
        audioRef.current.play().catch(() => {
          document.title = config.title;
        });
      }

      displayToast({
        status: config.status,
        description: config.label,
        actionLabel: t('common seeMore'),
        onClick: () => {
          navigate(href(config.route, { pharmacyId }));
        },
      });

      onNotificationHandled?.();
    },
    [emit, navigate, pharmacyId, onNotificationHandled, t]
  );

  return {
    handleNotification,
    audioRef,
  };
};
